import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Resizable } from 'react-resizable';
import { Table as AntTable, Empty, Checkbox, Dropdown, Button, Space } from 'antd';
import { SettingOutlined, MoreOutlined } from '@ant-design/icons';
import get from 'get-value';
import { getDisplayValue } from 'shared/utils/dimensionValue';
import useFormatMessage from 'hooks/useFormatMessage';
import useUser from 'hooks/useUser';
import { useMap } from 'ahooks';
import messages from './messages';

import './table.less';
import { SortHandle, SortableBody, SortableItem, getSorter } from './sorters';
import { getRenderer } from './renderers';

const getColumnKey = ({ dataIndex, key }) => {
  if (key) {
    return key;
  }

  if (Array.isArray(dataIndex)) {
    return dataIndex.join('.');
  }

  return dataIndex;
};

const ResizableTitle = props => {
  const { onResize, width, minWidth = 20, ...restProps } = props;
  const ref = useRef(null);
  const fallbackWidth = typeof minWidth === 'number' ? minWidth : 20;
  const [resizableWidth, setResizableWidth] = useState(
    typeof width === 'number' ? width : fallbackWidth,
  );

  useLayoutEffect(() => {
    if (typeof width === 'number') {
      setResizableWidth(width);
      return;
    }

    const elementWidth = ref.current?.offsetWidth;
    if (elementWidth) {
      setResizableWidth(elementWidth);
    }
  }, [width]);

  return (
    <Resizable
      width={resizableWidth}
      height={0}
      onResize={onResize}
      minConstraints={[fallbackWidth, 0]}
      draggableOpts={{ enableUserSelectHack: true }}
    >
      <th {...restProps} ref={ref} />
    </Resizable>
  );
};

const getAlign = (type) => {
  switch (type) {
    case 'number':
    case 'duration':
    case 'seconds':
    case 'milliseconds':
    case 'percent':
    case 'bytes':
      return 'right';
    case 'bool':
    case 'boolean':
      return 'center';
    default:
      return 'left';
  }
};

const getSearch = (type) => {
  switch (type) {
    case 'bool':
    case 'boolean':
      return false;
    default:
      return true;
  }
};

const mapColumns = ({ columns, getColumnWidth, onResize, rowKey, rowKeyColumn, disableSorting = false }) => {
  const cols = [];

  if (rowKeyColumn) {
    cols.push({
      dataIndex: rowKey,
      title: 'ID',
      visible: rowKeyColumn !== 'hidden',
    });
  }

  cols.push(...(columns?.filter(c => !c.inactive) || []));

  return cols.map((col) => {
    const key = getColumnKey(col);
    const width = getColumnWidth(key) || col.width;

    return {
      key,
      render: getRenderer(col.type),
      sorter: disableSorting ? undefined : getSorter(col.type)(col.dataIndex),
      align: getAlign(col.type),
      search: getSearch(col.type),
      onHeaderCell: onResize ? column => ({
        minWidth: column.minWidth,
        width,
        onResize: onResize(key),
      }) : undefined,
      ...col,
      width,
      children: col.children && mapColumns({ columns: col.children, getColumnWidth, onResize, rowKey, disableSorting: true }),
    };
  });
};

const Table = ({
  emptyMessage,
  columns,
  dataSource,
  search,
  interactive,
  pagination = true,
  resizable = true,
  actions,
  actionsMenuItems,
  footerActions,
  rowKey = 'id',
  rowKeyColumn = 'hidden',
  onSortEnd,
  hideHeader = false,
  onVisibleColumnsChange,
  components: componentsFromProps,
  ...props
}) => {
  const { isSuperUser } = useUser();
  const [filteredDataSource, setFilteredDataSource] = useState();
  const [columnWidths, { set: setColumnWidth, get: getColumnWidth }] = useMap([]);
  const onResize = useCallback(key => (e, { size }) => setColumnWidth(key, size.width));
  const mappedColumns = useMemo(() => mapColumns({
    columns,
    getColumnWidth,
    onResize: resizable ? onResize : null,
    rowKey,
    rowKeyColumn: isSuperUser ? rowKeyColumn : false,
    disableSorting: !!onSortEnd,
  }), [columns, columnWidths, rowKey, rowKeyColumn, isSuperUser, onSortEnd]);
  const [visibleColumns, setVisibleColumns] = useState();
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const $t = useFormatMessage();
  const ref = useRef();

  const activeColumns = useMemo(() => mappedColumns.filter(c => (visibleColumns ? visibleColumns.includes(c.key) : c.visible !== false)), [mappedColumns, visibleColumns]);

  useEffect(() => {
    if (onVisibleColumnsChange && visibleColumns?.length) {
      onVisibleColumnsChange(visibleColumns);
    }
  }, [visibleColumns]);

  useEffect(() => {
    if (search) {
      const searchableColumns = activeColumns.filter(c => c.search);
      const searchLowerCase = search.toLowerCase();

      const searchRow = (row) => searchableColumns.some((c) => {
        if (c.search === true) {
          const cell = getDisplayValue(get(row, c.dataIndex));
          return cell?.toLowerCase && cell.toLowerCase().search(searchLowerCase) !== -1;
        }
        return c.search(get(row, c.dataIndex), searchLowerCase, row);
      });

      const searchExpandedRowKeys = [];

      const recursiveSearch = (row) => {
        if (row.children) {
          const children = row.children.map(recursiveSearch).filter(x => x);
          if (children.length) {
            searchExpandedRowKeys.push(row[rowKey]);
            return {
              ...row,
              children,
            };
          }
        }

        if (searchRow(row)) {
          return row;
        }
        return false;
      };

      setFilteredDataSource(dataSource.reduce((arr, row) => {
        const filteredRow = recursiveSearch(row);
        if (filteredRow) {
          arr.push(filteredRow);
        }
        return arr;
      }, []));

      setExpandedRowKeys(searchExpandedRowKeys);
    }
  }, [dataSource, search, activeColumns]);

  const columnsBefore = [];
  const columnsAfter = [];

  if (interactive !== false || actions || actionsMenuItems) {
    columnsAfter.push({
      key: 'actions',
      align: 'right',
      fixed: 'right',
      filterIcon: <SettingOutlined />,
      filterDropdown: interactive !== false ? () => (
        <div style={{ padding: 8, maxHeight: 'calc(100vh - 250px)', overflow: 'auto' }}>
          <b>Columns</b>
          <Checkbox.Group
            value={visibleColumns || activeColumns.map(c => c.key)}
            onChange={setVisibleColumns}
          >
            {mappedColumns.filter(c => c.key).map(c => (<div key={c.key}><Checkbox value={c.key}>{c.title}</Checkbox></div>))}
          </Checkbox.Group>
        </div>
      ) : undefined,
      render: (_, record, index) => {
        let menu;
        const menuItems = actionsMenuItems && actionsMenuItems(record, index)?.reduce((arr, { visible, ...item }) => {
          if (visible !== false) {
            arr.push(item);
          }
          return arr;
        }, []);
        if (menuItems?.length) {
          menu = { items: menuItems };
        }

        return (
          <Space layout="horizontal">
            {actions && actions(record, index)}
            {menu && (
              <Dropdown placement="bottomRight" trigger={['click']} menu={menu}>
                <Button icon={<MoreOutlined />} />
              </Dropdown>
            )}
          </Space>
        );
      },
      width: 1,
    });
  }

  const components = useMemo(() => ({
    header: {
      row: hideHeader ? () => null : undefined,
      cell: resizable ? ResizableTitle : undefined,
    },
  }), [hideHeader, resizable]);

  const DraggableContainer = useCallback(sortableContainerProps => (
    <SortableBody
      useDragHandle
      lockAxis="y"
      disableAutoscroll
      helperClass="row-dragging"
      onSortEnd={onSortEnd}
      helperContainer={() => ref.current}
      {...sortableContainerProps}
    />
  ), [onSortEnd]);

  const DraggableRow = useCallback((rowProps) => {
    if (rowProps['data-row-key'] == null) return <tr {...rowProps} />;

    // function findIndex base on Table rowKey props and should always be a right array index
    const index = dataSource.findIndex(x => x[rowKey] === rowProps['data-row-key']);
    return <SortableItem index={index} {...rowProps} />;
  }, [dataSource]);

  if (onSortEnd) {
    components.body = {
      wrapper: DraggableContainer,
      row: DraggableRow,
    };
    columnsBefore.push({
      title: 'Sort',
      dataIndex: 'sort',
      width: 30,
      className: 'drag-visible',
      align: 'center',
      render: (_, record) => record[rowKey] != null && <SortHandle />,
    });
  }

  const allColumns = [...columnsBefore, ...activeColumns, ...columnsAfter];

  const usedDataSource = search ? filteredDataSource : dataSource;

  const mergedComponents = {
    ...components,
    ...componentsFromProps,
    header: {
      ...components?.header,
      ...componentsFromProps?.header,
    },
    body: {
      ...components?.body,
      ...componentsFromProps?.body,
    },
  };

  return (
    <div ref={ref}>
      <AntTable
        columns={allColumns}
        dataSource={usedDataSource}
        locale={{
          emptyText: <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={emptyMessage}
          />,
        }}
        pagination={pagination && {
          position: ['bottomLeft'],
          showTotal: (total, range) => <FormattedMessage {...messages.paginationTotal} values={{ start: range[0], end: range[1], total }} />,
          locale: {
            items_per_page: $t(messages.paginationPerPage),
          },
          ...(pagination === true ? {} : pagination),
        }}
        components={mergedComponents}
        rowClassName={() => 'editable-row'}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: setExpandedRowKeys,
        }}
        rowKey={rowKey}
        {...props}
      />
      {footerActions && (
        pagination && usedDataSource?.length ? <div style={{ float: 'right', marginTop: '-48px' }}>{footerActions}</div> : <div style={{ float: 'right', marginTop: '1em' }}>{footerActions}</div>
      )}
    </div>
  );
};

Table.Summary = AntTable.Summary;

export default Table;
